/**
 * FormHeader Component
 *
 * Provides a consistent header pattern across all forms with Logo, optional icon,
 * translated title, and translated description.
 *
 * @example
 * ```tsx
 * import { FormHeader } from '@/components/common/FormHeader';
 * import { Building } from 'lucide-react';
 *
 * <FormHeader
 *   titleKey="branch.title"
 *   descriptionKey="branch.description"
 *   icon={Building}
 * />
 * ```
 */

import { Logo } from '@/components';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { FormHeaderProps } from '@/types/formHeader.types';
import React from 'react';

export const FormHeader: React.FC<FormHeaderProps> = ({
  titleKey,
  descriptionKey,
  icon: Icon,
  className,
  logoSize = 'xl',
  iconSize = 'h-8 w-8',
}) => {
  const { t } = useFormsTranslation();

  return (
    <div className={cn('flex items-center', className)}>
      {/* Logo */}
      <Logo size={logoSize} className='shrink-0 pr-2' />

      {/* Title and Description Section */}
      <div className='grow flex flex-col items-center justify-center'>
        <h1 className='text-3xl font-bold tracking-tight flex items-center gap-3'>
          {/* Optional Icon */}
          {Icon && (
            <Icon className={cn('text-primary', iconSize)} aria-hidden='true' />
          )}

          {/* Translated Title */}
          {t(titleKey)}
        </h1>

        {/* Translated Description */}
        <p className='text-muted-foreground mt-2'>{t(descriptionKey)}</p>
      </div>
    </div>
  );
};

// Named export for consistency
export default FormHeader;
